/**
 * 🧮 원가분석표 API — backend /price-list/cost-sheets/* 와 1:1 (v2.234.0 · 영업건 연결 v2.236.0)
 *   원가가 실리므로 영업 이상만(viewer 는 가격표 메뉴 자체가 닫혀 있다 · 서버도 sales+ 가드).
 */
import type { ApiResponse } from './types';
import { normalizeLine, type CostLine } from './cost-sheet-calc';

const BASE = '/api';

export interface CostSheet {
  id: number;
  title: string;
  customer: string;
  memo: string;
  marginPct: number;
  roundUnit: number;
  lines: CostLine[];
  /** 이 표로 만든 견적서 (없으면 null · 견적서를 지우면 연결만 끊긴다) */
  quoteId: number | null;
  quoteNo: string | null;
  /** 연결 영업건 (v2.236.0 · 없으면 null · 영업건을 지우면 연결만 끊긴다) — 번호와 함께 프로젝트명·엔드유저·제안가가 따라온다 */
  salesId: string | null;
  salesProjectName: string | null;
  salesEndUser: string | null;
  salesProposalPrice: number | null;
  createdBy: string | null;
  createdAt: string;
  updatedBy: string | null;
  updatedAt: string;
}

export interface CostSheetInput {
  title: string;
  customer: string;
  memo: string;
  marginPct: number;
  roundUnit: number;
  lines: CostLine[];
  quoteId?: number | null;
  salesId?: string | null;
}

function fromServer(r: CostSheet): CostSheet {
  return { ...r, lines: Array.isArray(r.lines) ? r.lines.map(normalizeLine) : [] };
}

/** 목록 — salesId 를 주면 그 영업건의 표만 (영업건 상세의 [🧮 원가분석] 단추가 「몇 건 있나」를 볼 때) */
export async function listCostSheets(params?: { salesId?: string }): Promise<CostSheet[]> {
  const qs = params?.salesId ? `?salesId=${encodeURIComponent(params.salesId)}` : '';
  const res = await fetch(`${BASE}/price-list/cost-sheets${qs}`, { credentials: 'include' });
  const json = (await res.json()) as ApiResponse<CostSheet[]>;
  if (!json.ok) throw new Error(json.error.message);
  return json.data.map(fromServer);
}

export async function createCostSheet(input: CostSheetInput): Promise<CostSheet> {
  const res = await fetch(`${BASE}/price-list/cost-sheets`, {
    method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(input),
  });
  const json = (await res.json()) as ApiResponse<CostSheet>;
  if (!json.ok) throw new Error(json.error.message);
  return fromServer(json.data);
}

export async function updateCostSheet(id: number, patch: Partial<CostSheetInput>): Promise<CostSheet> {
  const res = await fetch(`${BASE}/price-list/cost-sheets/${id}`, {
    method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(patch),
  });
  const json = (await res.json()) as ApiResponse<CostSheet>;
  if (!json.ok) throw new Error(json.error.message);
  return fromServer(json.data);
}

export async function deleteCostSheet(id: number): Promise<void> {
  const res = await fetch(`${BASE}/price-list/cost-sheets/${id}`, { method: 'DELETE', credentials: 'include' });
  const json = (await res.json()) as ApiResponse<{ id: number }>;
  if (!json.ok) throw new Error(json.error.message);
}

/** 원가분석표 화면 주소 — 다른 화면(견적서 탭 · 미리보기 · 영업건 상세)에서 올 때 */
export const costSheetUrl = (sheetId: number): string => `/price-list?tab=cost&sheet=${sheetId}`;
export const costSheetForSalesUrl = (salesId: string): string => `/price-list?tab=cost&sales=${encodeURIComponent(salesId)}`;
